import { motion } from "framer-motion";
import { CheckCircle2, MessageCircle, Phone, Recycle } from "lucide-react";
import { IMAGES, PHONE_DISPLAY, PHONE_TEL, reportConversion, waLink } from "../../data/site";
import { EASE } from "./Reveal";

const MASK_EASE = [0.76, 0, 0.24, 1];

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "112%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 0.95, ease: MASK_EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

const HERO_CHECKS = ["10+ Years Experience", "1000+ Happy Clients", "Same-Day Pickup"];
const QUOTE_CHIPS = ["Free Pickup", "Cash on Spot", "All Bangalore"];

export default function KineticHero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden bg-gradient-to-br from-brand-deep via-brand to-brand-dark"
    >
      <img
        src={IMAGES.hero}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-15"
      />
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-r from-brand-deep/80 via-transparent to-brand-deep/70"
        aria-hidden="true"
      />

      <div className="relative mx-auto grid w-full max-w-7xl items-center gap-14 px-5 pb-20 pt-28 sm:px-8 md:pt-36 lg:grid-cols-2">
        <div>
          <motion.span
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE, delay: 0.05 }}
            data-testid="hero-overline"
            className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-white"
          >
            <Recycle className="h-3.5 w-3.5" />
            Scrap Buyers in Bangalore
          </motion.span>

          <h1 className="mt-6 font-display text-[2.05rem] font-extrabold leading-[1.12] tracking-tight text-white sm:text-5xl sm:leading-[1.08] lg:text-[3.6rem]">
            <MaskedLine delay={0.15}>We Buy All Types of</MaskedLine>
            <MaskedLine delay={0.3}>Scrap in Bangalore</MaskedLine>
          </h1>

          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 0.5, ease: EASE, delay: 0.5 }}
            className="mt-5 h-1 w-12 origin-left rounded-full bg-white"
            aria-hidden="true"
          />

          <p className="mt-4 font-display text-[2.05rem] font-extrabold leading-[1.12] tracking-tight text-tangy sm:text-5xl sm:leading-[1.08] lg:text-[3.6rem]">
            <MaskedLine delay={0.55}>Best Price</MaskedLine>
            <MaskedLine delay={0.7}>Guaranteed.</MaskedLine>
          </p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.85 }}
            className="mt-6 max-w-lg text-base leading-relaxed text-white/85 sm:text-lg"
            data-testid="hero-subheadline"
          >
            Instant Pickup • Same-Day Payment • Free Inspection. Sell your scrap to Bangalore's
            most trusted scrap buyers — call now and get a quote in minutes.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 1 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              href={PHONE_TEL}
              onClick={reportConversion}
              data-testid="hero-call-button"
              className="inline-flex items-center gap-2.5 rounded-full bg-tangy px-7 py-3.5 text-base font-semibold text-white shadow-lg shadow-black/20 transition-transform duration-200 hover:scale-[1.03]"
            >
              <Phone className="h-5 w-5" />
              Call {PHONE_DISPLAY}
            </a>
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={reportConversion}
              data-testid="hero-whatsapp-button"
              className="inline-flex items-center gap-2.5 rounded-full bg-wa px-7 py-3.5 text-base font-semibold text-white shadow-lg shadow-black/20 transition-transform duration-200 hover:scale-[1.03]"
            >
              <MessageCircle className="h-5 w-5" />
              WhatsApp Now
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3"
            data-testid="hero-trust-row"
          >
            {HERO_CHECKS.map((item) => (
              <span key={item} className="flex items-center gap-2 text-sm font-medium text-white">
                <CheckCircle2 className="h-5 w-5 text-white/80" />
                {item}
              </span>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 48 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.5 }}
          className="relative"
        >
          <div className="rounded-[2.5rem] border border-white/20 bg-white/10 p-2.5 backdrop-blur-sm">
            <div
              className="rounded-[2rem] bg-white p-8 shadow-2xl shadow-black/25 sm:p-10"
              data-testid="hero-quote-card"
            >
              <div className="flex items-center gap-2 text-brand">
                <Phone className="h-4 w-4" />
                <span className="text-xs font-bold uppercase tracking-[0.2em]">
                  Get Instant Quote
                </span>
              </div>
              <a
                href={PHONE_TEL}
                onClick={reportConversion}
                data-testid="hero-phone-number"
                className="mt-4 block whitespace-nowrap font-display text-[1.65rem] font-extrabold tracking-tight text-ink sm:text-4xl"
              >
                {PHONE_DISPLAY}
              </a>
              <p className="mt-2 text-sm text-steel">Tap below — we'll respond in minutes.</p>

              <div className="mt-6 space-y-3">
                <a
                  href={PHONE_TEL}
                  onClick={reportConversion}
                  data-testid="quote-call-button"
                  className="flex items-center justify-center gap-2.5 rounded-2xl bg-brand-dark py-4 text-base font-semibold text-white transition-colors duration-200 hover:bg-brand-deep"
                >
                  <Phone className="h-5 w-5" />
                  Call Now
                </a>
                <a
                  href={waLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={reportConversion}
                  data-testid="quote-whatsapp-button"
                  className="flex items-center justify-center gap-2.5 rounded-2xl bg-wa py-4 text-base font-semibold text-white transition duration-200 hover:brightness-95"
                >
                  <MessageCircle className="h-5 w-5" />
                  WhatsApp Now
                </a>
              </div>

              <div className="mt-6 flex flex-wrap justify-center gap-2">
                {QUOTE_CHIPS.map((chip) => (
                  <span
                    key={chip}
                    className="rounded-full bg-green-50 px-3.5 py-1.5 text-xs font-semibold text-brand-dark"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
